import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api } from "../lib/api";
import { COMPANY } from "../data/site";

const INITIAL = { nome: "", email: "", telefono: "", oggetto: "", messaggio: "" };

export default function Contatti() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/contatti", form);
      toast.success("Richiesta inviata", {
        description: "Ti risponderemo entro 24 ore lavorative.",
      });
      setForm(INITIAL);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Si è verificato un errore. Riprova tra qualche istante.";
      toast.error("Invio non riuscito", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Contatti"
        title="Parliamo del vostro cantiere."
        description="Compila il modulo o contattaci direttamente: ti risponderemo entro 24 ore lavorative."
      />

      <section data-reveal className="py-16 md:py-24 bg-white border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16">
          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 space-y-6"
          >
            <div className="overline mb-2">/ Invia richiesta</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600 block mb-2">
                  Nome *
                </label>
                <Input
                  required
                  minLength={2}
                  data-testid="contact-nome"
                  value={form.nome}
                  onChange={update("nome")}
                  placeholder="Mario Rossi"
                  className="rounded-xl border border-neutral-300 h-12 px-4 focus-visible:ring-2 focus-visible:ring-[#1D4ED8]/20 focus-visible:border-[#1D4ED8] text-base"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600 block mb-2">
                  Email *
                </label>
                <Input
                  required
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="mario@example.it"
                  className="rounded-xl border border-neutral-300 h-12 px-4 focus-visible:ring-2 focus-visible:ring-[#1D4ED8]/20 focus-visible:border-[#1D4ED8] text-base"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600 block mb-2">
                  Telefono
                </label>
                <Input
                  data-testid="contact-telefono"
                  value={form.telefono}
                  onChange={update("telefono")}
                  placeholder="+39 ..."
                  className="rounded-xl border border-neutral-300 h-12 px-4 focus-visible:ring-2 focus-visible:ring-[#1D4ED8]/20 focus-visible:border-[#1D4ED8] text-base"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600 block mb-2">
                  Oggetto *
                </label>
                <Input
                  required
                  minLength={2}
                  data-testid="contact-oggetto"
                  value={form.oggetto}
                  onChange={update("oggetto")}
                  placeholder="Richiesta preventivo — lavori stradali"
                  className="rounded-xl border border-neutral-300 h-12 px-4 focus-visible:ring-2 focus-visible:ring-[#1D4ED8]/20 focus-visible:border-[#1D4ED8] text-base"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600 block mb-2">
                Messaggio *
              </label>
              <Textarea
                required
                minLength={5}
                data-testid="contact-messaggio"
                value={form.messaggio}
                onChange={update("messaggio")}
                placeholder="Descrivici il tuo progetto o intervento..."
                rows={7}
                className="rounded-2xl border border-neutral-300 px-4 py-3 focus-visible:ring-2 focus-visible:ring-[#1D4ED8]/20 focus-visible:border-[#1D4ED8] text-base resize-none"
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 bg-[#0B1E3F] hover:bg-[#1D4ED8] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full font-display font-bold text-sm tracking-wider uppercase transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Invio in corso...
                  </>
                ) : (
                  <>
                    Invia richiesta <Send className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="mt-4 text-xs text-neutral-500 max-w-md">
                Inviando il modulo accetti il trattamento dei dati ai fini della gestione della
                richiesta. Non utilizziamo i tuoi dati per finalità di marketing.
              </p>
            </div>
          </form>

          {/* Info */}
          <aside className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-neutral-200">
            <div className="overline mb-6">/ Recapiti</div>
            <h3 className="hero-title text-3xl md:text-4xl mb-10">Scrivici.<br />O chiamaci.</h3>

            <div className="space-y-8">
              <div className="flex items-start gap-5 border-t border-neutral-200 pt-6">
                <div className="w-10 h-10 bg-[#0B1E3F] text-white flex-shrink-0 flex items-center justify-center rounded-xl">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">
                    Sede
                  </div>
                  <div className="font-display font-semibold text-lg">{COMPANY.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-5 border-t border-neutral-200 pt-6">
                <div className="w-10 h-10 bg-[#0B1E3F] text-white flex-shrink-0 flex items-center justify-center rounded-xl">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">
                    Telefono
                  </div>
                  <a
                    href={`tel:${COMPANY.phoneRaw}`}
                    className="font-display font-semibold text-lg hover:text-[#1D4ED8]"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5 border-t border-neutral-200 pt-6">
                <div className="w-10 h-10 bg-[#0B1E3F] text-white flex-shrink-0 flex items-center justify-center rounded-xl">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">
                    Email
                  </div>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="font-display font-semibold text-lg hover:text-[#1D4ED8] break-all"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5 border-t border-neutral-200 pt-6">
                <div className="w-10 h-10 bg-[#0B1E3F] text-white flex-shrink-0 flex items-center justify-center rounded-xl">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">
                    Orari ufficio
                  </div>
                  <div className="font-display font-semibold text-lg">{COMPANY.hours}</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Map */}
      <section className="bg-[#F5F5F3] py-0">
        <div className="w-full h-[400px] md:h-[500px] border-t border-neutral-200 relative">
          <iframe
            title="Mappa sede Fratelli D'Ambrosio S.R.L. — Via F. Rosazza 4, Sagliano Micca (BI)"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${COMPANY.mapLng - 0.018}%2C${COMPANY.mapLat - 0.008}%2C${COMPANY.mapLng + 0.018}%2C${COMPANY.mapLat + 0.008}&layer=mapnik&marker=${COMPANY.mapLat}%2C${COMPANY.mapLng}`}
            className="w-full h-full filter contrast-110"
            loading="lazy"
          />
          <a
            href={`https://www.openstreetmap.org/?mlat=${COMPANY.mapLat}&mlon=${COMPANY.mapLng}#map=17/${COMPANY.mapLat}/${COMPANY.mapLng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-md text-[#0B1E3F] px-5 py-3 rounded-full font-display font-bold text-xs tracking-wider uppercase hover:bg-[#1D4ED8] hover:text-white transition-colors shadow-lg"
            data-testid="map-open-link"
          >
            Apri in mappe
          </a>
        </div>
      </section>
    </>
  );
}
